import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';

export default function PinPage() {
  const navigate = useNavigate();
  const { confirmPin, forgotPin, isLoading, error, clearError } = useAuth();
  const { isDesktop } = useResponsive();
  const inputRefs = useRef([]);
  const [pinValue, setPinValue] = useState(['', '', '', '']);
  const [loadingPhase, setLoadingPhase] = useState(null); // 'verifying' | 'loading-profile' | null

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Clear PIN when there's an error
  useEffect(() => {
    if (error) {
      setPinValue(['', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  }, [error]);

  // Auto-submit when all 4 digits are entered
  const submitPin = async (pin) => {
    if (isLoading || loadingPhase) return;

    try {
      setLoadingPhase('verifying');
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingPhase('loading-profile');
      await confirmPin(pin);
      navigate('/home');
    } catch (err) {
      console.error('PIN verification error:', err);
      setLoadingPhase(null);
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // Clear error when user starts typing
    if (error) clearError();

    const newPin = [...pinValue];
    newPin[index] = value.slice(-1);
    setPinValue(newPin);

    // Auto-advance to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 4 digits are filled
    if (value && index === 3 && newPin.every((digit) => digit !== '')) {
      submitPin(newPin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleForgotPin = async () => {
    try {
      await forgotPin();
      alert('OTP sent to your registered contact. PIN reset flow coming soon.');
    } catch (err) {
      console.error('Forgot PIN error:', err);
    }
  };

  // Show full-screen loading overlay when loading profile
  if (loadingPhase === 'loading-profile') {
    return (
      <div className="min-h-screen min-h-dvh flex flex-col bg-white">
        {/* Header - matches global header (logo icon only) */}
        <header className="bg-white px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center border-b border-gray-100">
          <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
        </header>
        {/* Centered pulsing logo */}
        <div className="flex-1 flex items-center justify-center">
          <img
            src="/logo/bonndleLogoIcon_Indigo.svg"
            alt="Bonndle"
            className="w-16 h-16 animate-pulse"
            style={{ animationDuration: '1.5s' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={() => navigate('/signin')}
            disabled={isLoading || loadingPhase}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Security PIN</h1>
        </header>

        {/* Content */}
        <div className="flex-1 px-6 py-8 flex flex-col">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-indigo-600" size={28} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Enter your PIN</h2>
            <p className="text-gray-600">
              Enter your 4-digit security PIN to access your account.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          {/* PIN Input */}
          <div className="flex justify-center gap-4 mb-8">
            {pinValue.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading || loadingPhase}
                className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none transition-colors ${
                  error
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-200 focus:border-indigo-500'
                } ${isLoading || loadingPhase ? 'bg-gray-100' : ''}`}
              />
            ))}
          </div>

          <p className="text-center text-sm text-gray-500">
            Forgot your PIN?{' '}
            <button
              type="button"
              onClick={handleForgotPin}
              disabled={isLoading || loadingPhase}
              className="text-indigo-600 font-semibold hover:underline disabled:opacity-50"
            >
              Reset it
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
